import React from 'react';
import Modal from '@components/elements/modal/modal';
import MenuIncentiveModal from '@components/products/menu-incentive-modal/menu-incentive-modal';
import ProductCardComponent from '@components/products/product-card/product-card';
import ProductList from '@components/products/product-list/product-list';
import ProductTaxonsNavigation from '@components/products/product-taxons-navigation/product-taxons-navigation';
import ProductsTaxon from '@components/products/products-taxon/products-taxon';
import ShippingModal from '@components/shipping/shipping-modal/shipping-modal';
import {
  v2_FallbackProvider as FallbackProvider,
  v2_ProductList,
  v2_useAddToCart,
  v2_useCart,
  v2_useProductList,
  v2_useShippingSelectionModal,
} from '@crocodille/common';
import { assertSome, isSome, Maybe } from '@crocodille/util';
import PageMeta from '@framework/page-meta';
import { useRouter } from '@framework/router';
import {
  getAppLayoutWithCart,
  useAppLayoutState,
} from '@layouts/app-layout/app-layout';
import { analyticsEvent } from '@modules/analytics';
import * as routing from '@routing';
import { Children, useCallback, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useClearProductConfiguration } from '@crocodille/common/lib/modules/products.v2';

export const ProductCard: React.FC<{
  readonly cart: ReturnType<typeof v2_useCart>['data'];
  readonly product: v2_ProductList[number]['products'][number];
  readonly taxon: Omit<v2_ProductList[number], 'products'>;
  onShowIncentiveModal: (slug: string) => void;
  readonly bigCardItems?: boolean;
}> = ({ cart, taxon, product, onShowIncentiveModal, bigCardItems }) => {
  const { push } = useRouter();
  const { addVariantToCart } = v2_useAddToCart(product);
  const { toggleCartOpen } = useAppLayoutState();

  const isOnlyPickup =
    (!cart || cart.deliveryMethodType === 'courier') &&
    product.conditionallyAvailable?.includes?.('unavailable_for_delivery');

  const isOnlyDelivery =
    (!cart || cart.deliveryMethodType === 'atBranch') &&
    product.conditionallyAvailable?.includes?.('unavailable_for_pickup');

  const defaultVariant = assertSome(
    product.variants?.find((variant) => variant.default) ?? product.variants[0],
  );

  const handleAddToCart = async () => {
    if (
      product.configurationStyle === 'configurator' ||
      product.configurationStyle === 'custom_menu' ||
      product.configurationStyle === 'simple' ||
      product.variants.length > 1
    ) {
      push(
        routing.products({
          taxon: taxon.slug,
          product: product.slug,
        }),
        undefined,
        { scroll: false },
      );
    } else if (product.configurationStyle === 'incentive_only') {
      onShowIncentiveModal(product.slug);
    } else {
      const added = await addVariantToCart(defaultVariant.code);
      if (added) {
        toggleCartOpen(true);

        analyticsEvent('add_to_cart', {
          ecommerce: {
            items: [
              {
                item_id: defaultVariant.code,
                item_name: product.title,
                price: ((defaultVariant.price ?? 0) / 100).toFixed(2),
                item_category: taxon.name,
                item_list_name: taxon.name,
                quantity: '1',
                index: 1,
              },
            ],
          },
        });

        analyticsEvent('remarketing_add_to_cart', {
          value: (defaultVariant.price ?? 0) / 100,
          items: [
            {
              id: product.id.toString(),
              google_business_vertical: 'retail',
              quantity: 1,
            },
          ],
        });
      }
    }
  };

  return (
    <ProductCardComponent
      id={product.id}
      title={product.title}
      price={cart ? defaultVariant.price : null}
      image={Array.isArray(product.image) ? product.image[0] : product.image}
      description={product.shortDescription}
      isOnlyPickup={isOnlyPickup}
      isOnlyDelivery={isOnlyDelivery}
      isSpecialItem={Boolean(
        product.tags?.find((tag) => tag.id === 'daily_baguette'),
      )}
      isAvailable={product.available}
      slug={product.slug}
      tags={product.tags.map((tag) => tag.id) ?? undefined}
      onAddToCart={handleAddToCart}
      bigCardItems={bigCardItems}
      taxon={taxon}
    />
  );
};

const MenuLayout: React.FC<
  React.PropsWithChildren<{
    readonly branch: string;
    readonly taxon: string;
    readonly product?: Maybe<string>;
  }>
> = ({
  children,
  branch: branchCode,
  taxon: taxonSlugProp,
  product: productSlugProp,
}) => {
  const { t } = useTranslation();
  const { query, push } = useRouter();

  const taxonSlug =
    typeof query.taxon === 'string' ? query.taxon : taxonSlugProp;
  const productSlug =
    typeof query.slug === 'string' ? query.slug : productSlugProp;

  const { data: cart } = v2_useCart();
  const { data: taxons } = v2_useProductList({
    branchCode: cart?.branchCode ?? branchCode,
  });

  const taxon = taxons?.find((taxon) => taxon.slug === taxonSlug);

  const taxonsRef = useRef(taxons);
  taxonsRef.current = taxons;

  const impressionsRef = useRef(new Set<number>());

  const handleProductsShown = useCallback((productIds: readonly number[]) => {
    if (!taxonsRef.current) {
      return;
    }

    const productsWithTaxons = taxonsRef.current
      .flatMap((taxon) => {
        return taxon.products.map((product) => ({
          product,
          taxon,
        }));
      })
      .map(({ product, taxon }, index) => ({ product, taxon, index }))
      .filter(
        ({ product }) =>
          productIds.includes(product.id) &&
          !impressionsRef.current.has(product.id),
      );

    for (const productId of productIds) {
      impressionsRef.current.add(productId);
    }

    if (productsWithTaxons.length === 0) {
      return;
    }

    analyticsEvent('view_item_list', {
      ecommerce: {
        items: productsWithTaxons.map(({ product, taxon, index }) => {
          const defaultVariant =
            product.variants?.find((variant) => variant.default) ??
            product.variants[0];

          return {
            item_name: product.title,
            item_id: defaultVariant?.code ?? '',
            price: ((defaultVariant?.price ?? 0) / 100).toFixed(2),
            item_category: taxon.name,
            item_list_name: taxon.name,
            index: index + 1,
          };
        }),
      },
    });
  }, []);

  const { show: showShippingModal, close: closeShippingModal } =
    v2_useShippingSelectionModal();

  const [showIncentiveModal, setShowIncentiveModal] = useState<string | null>(
    null,
  );

  const clearConfiguration = useClearProductConfiguration();

  return (
    <>
      {Children.count(children) === 0 ? (
        <PageMeta pageTitle={taxon?.name ?? ''} description={''} />
      ) : null}
      <ProductList
        taxonSlug={taxonSlug}
        onProductsShown={handleProductsShown}
        disableRouteReplace={isSome(productSlug)}
      >
        <ProductList.Navigation>
          <ProductTaxonsNavigation>
            <ProductTaxonsNavigation.Item
              label={t('menu_page.title')}
              href={routing.menu()}
              state="emphasized"
            />
            {(taxons ?? []).map((taxon) => (
              <React.Fragment key={taxon.id}>
                {taxon.tags?.includes?.('gift_cards') ? (
                  <ProductTaxonsNavigation.Item
                    label={taxon.name}
                    href={routing.products({
                      taxon: taxon.slug,
                    })}
                  />
                ) : (
                  <ProductTaxonsNavigation.Item
                    label={taxon.name}
                    href={routing.products({ taxon: taxon.slug })}
                    shallow
                    state={taxon.slug === taxonSlug ? 'active' : 'inactive'}
                    onClick={(e) => {
                      e.preventDefault();

                      document
                        .querySelector(`#${taxon.slug} .scroll-anchor`)
                        ?.scrollIntoView({
                          block: 'start',
                          behavior: 'smooth',
                        });
                    }}
                  />
                )}
              </React.Fragment>
            ))}
          </ProductTaxonsNavigation>
        </ProductList.Navigation>
        {taxons?.map((taxon) => {
          return (
            <ProductsTaxon
              key={taxon.id}
              id={taxon.id}
              title={taxon?.name ?? ''}
              slug={taxon?.slug ?? ''}
            >
              {taxon.products.map((product) => {
                return (
                  <ProductCard
                    key={product.id}
                    cart={cart}
                    taxon={taxon}
                    product={product}
                    onShowIncentiveModal={setShowIncentiveModal}
                  />
                );
              })}
            </ProductsTaxon>
          );
        })}
      </ProductList>
      <Modal
        open={Children.count(children) > 0}
        onClose={() => {
          clearConfiguration();
          push(routing.products({ taxon: taxonSlug }), undefined, {
            scroll: false,
          });
        }}
        title={''}
        height="fill"
        width="auto"
        transparent
        showCloseButton={true}
        hideTitle={true}
      >
        {children}
      </Modal>
      <ShippingModal open={showShippingModal} onClose={closeShippingModal} />
      {showIncentiveModal ? (
        <MenuIncentiveModal
          productSlug={showIncentiveModal}
          onClose={() => setShowIncentiveModal(null)}
        />
      ) : null}
    </>
  );
};

export function getMenuLayout(
  children: React.ReactNode,
  fallback: Record<string, unknown>,
  branchCode: string,
  taxonSlug: string,
  productSlug?: string,
) {
  return getAppLayoutWithCart(
    <FallbackProvider fallback={fallback}>
      <MenuLayout branch={branchCode} taxon={taxonSlug} product={productSlug}>
        {children}
      </MenuLayout>
    </FallbackProvider>,
  );
}

export default MenuLayout;
